import { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '@services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const [loading, setLoading] = useState(true);

  // Verificar si hay sesión al cargar la app
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      setIsGuest(false);
    }
    setLoading(false);
  }, []);

  /**
   * Inicia sesión de usuario
   */
  const login = async (credentials) => {
    try {
      const response = await authService.login(credentials);
      setUser(response.usuario);
      setIsGuest(false);
      return { success: true, data: response };
    } catch (error) {
      console.error('Error en login:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al iniciar sesión' 
      };
    }
  };

  /**
   * Registra un nuevo usuario
   */
  const register = async (userData) => {
    try {
      const response = await authService.register(userData);
      setUser(response.usuario);
      setIsGuest(false);
      return { success: true, data: response };
    } catch (error) {
      console.error('Error en registro:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Error al registrar usuario' 
      };
    }
  };

  /**
   * Continuar como invitado (sin guardar datos)
   */
  const continueAsGuest = () => {
    setIsGuest(true);
    setUser(null);
  };

  /**
   * Cierra sesión
   */
  const logout = () => {
    authService.logout();
    setUser(null);
    setIsGuest(false);
  };

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    isGuest,
    loading,
    login,
    register,
    logout,
    continueAsGuest,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
